import React, { useState, useEffect } from 'react';
import { Search, Loader2, Calendar, User, DollarSign, Tag } from 'lucide-react';
import { API_BASE_URL } from '../../config';

const toNumber = (value, fallback = 0) => {
    if (value == null || value === '') return fallback;
    if (typeof value === 'number') return Number.isFinite(value) ? value : fallback;
    const parsed = Number(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : fallback;
};

const Tickets = ({ authToken }) => {
    const [tickets, setTickets] = useState([]);
    const [ticketCategories, setTicketCategories] = useState([]); // id -> name for resolving ticket_type
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Fetch platform ticket categories (id -> name) so we can show name when API returns id in ticket_type
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/admin/super/ticket-pricing`, {
                    headers: { Authorization: `Bearer ${authToken}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    const list = Array.isArray(data) ? data : (data.ticket_categories || data.categories || []);
                    setTicketCategories(list);
                } else {
                    const fallback = await fetch(`${API_BASE_URL}/store/ticket-categories`).then(r => r.ok ? r.json() : null).catch(() => null);
                    if (fallback?.ticket_categories) setTicketCategories(fallback.ticket_categories);
                }
            } catch (_) {}
        };
        fetchCategories();
    }, [authToken]);

    useEffect(() => {
        const fetchTickets = async () => {
            try {
                setLoading(true);
                setError('');
                const response = await fetch(`${API_BASE_URL}/admin/super/tickets`, {
                    headers: { Authorization: `Bearer ${authToken}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    const raw = data.tickets || (Array.isArray(data) ? data : []);
                    // Normalize to actual ticket display shape (no raw rows or mixed keys)
                    const ticketsData = (raw || [])
                        .filter((t) => t && typeof t === 'object' && !Array.isArray(t))
                        .map((t) => {
                            const eventObj = t.event && typeof t.event === 'object' ? t.event : {};
                            const quantity = Math.max(1, toNumber(t.quantity ?? t.qty ?? t.count, 1));
                            const amount = toNumber(t.amount ?? t.total_amount ?? t.total, 0);
                            const price = toNumber(
                                t.price ?? t.unit_price ?? (amount > 0 ? amount / quantity : 0),
                                0
                            );

                            return {
                                id: t.id,
                                purchase_id: t.purchase_id,
                                order_id: t.order_id ?? t.orderId,
                                customer_name: t.customer_name ?? t.name ?? t.user_name ?? t.buyer_name ?? t.customer?.name ?? '',
                                customer_email: t.customer_email ?? t.email ?? t.user_email ?? t.buyer_email ?? t.customer?.email ?? '',
                                event_name: t.event_name ?? t.event_title ?? eventObj.title ?? eventObj.name ?? '',
                                event: eventObj,
                                event_ticket_id: t.event_ticket_id,
                                ticket_type: t.ticket_type ?? t.ticket_type_name ?? t.category_name ?? '',
                                ticket_type_name: t.ticket_type_name ?? t.category_name ?? '',
                                quantity,
                                amount,
                                price,
                                status: t.status ?? t.ticket_status ?? t.payment_status ?? 'pending',
                                payment_status: t.payment_status ?? t.status ?? t.ticket_status ?? 'pending',
                                created_at: t.created_at ?? t.purchase_date ?? t.date,
                                purchase_date: t.purchase_date ?? t.created_at ?? t.date
                            };
                        });
                    setTickets(ticketsData);
                } else {
                    const errData = await response.json().catch(() => ({}));
                    setError(errData.error || 'Failed to load ticket purchases');
                    setTickets([]);
                }
            } catch (err) {
                setError('Failed to fetch ticket purchases');
                setTickets([]);
            } finally {
                setLoading(false);
            }
        };
        fetchTickets();
    }, [authToken]);

    const getCustomerDisplay = (t) => {
        if (!t) return '—';
        const name = t.customer_name ?? t.name ?? t.user_name ?? t.buyer_name;
        if (name && String(name).trim()) return String(name).trim();
        const first = t.first_name ?? t.billing_first_name;
        const last = t.last_name ?? t.billing_last_name;
        const combined = [first, last].filter(Boolean).map(String).join(' ').trim();
        if (combined) return combined;
        return t.customer_email ?? t.email ?? '—';
    };

    const getEventDisplay = (t) => {
        if (!t) return '—';
        return t.event_name ?? t.event_title ?? t.event?.title ?? t.event?.name ?? '—';
    };

    // Resolve ticket type to display name: API may send name (COALESCE(tc.name, et.ticket_type)) or slug/id; resolve via categories when needed.
    const getTicketTypeName = (ticket, fallbackType) => {
        const explicitName = ticket?.ticket_type_name ?? ticket?.category_name;
        if (explicitName != null && String(explicitName).trim()) return String(explicitName).trim();
        const ticketType = fallbackType ?? ticket?.ticket_type ?? ticket?.ticket_type_name ?? ticket?.event_ticket_id ?? '';
        if (ticketType == null || ticketType === '') return 'General';
        const str = String(ticketType).trim();
        if (!str) return 'General';
        // Numeric id: look up by category id
        if (/^\d+$/.test(str)) {
            const id = parseInt(str, 10);
            const cat = ticketCategories.find(c => c.id === id);
            return (cat && cat.name) ? cat.name : str;
        }
        // Slug or key: look up by slug or by name (case-insensitive)
        const slugLower = str.toLowerCase();
        const bySlug = ticketCategories.find(c => (c.slug || '').toLowerCase() === slugLower);
        if (bySlug && bySlug.name) return bySlug.name;
        const byName = ticketCategories.find(c => (c.name || '').toLowerCase() === slugLower);
        if (byName && byName.name) return byName.name;
        // Backend now returns name in ticket_type when JOIN with ticket_categories; use as-is, or title-case single words
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    const filteredTickets = tickets.filter(t => {
        const typeName = getTicketTypeName(t, t.ticket_type ?? t.ticket_type_name);
        const customerDisplay = getCustomerDisplay(t);
        const eventDisplay = getEventDisplay(t);
        const term = searchTerm.toLowerCase();
        return (
            eventDisplay.toLowerCase().includes(term) ||
            customerDisplay.toLowerCase().includes(term) ||
            typeName.toLowerCase().includes(term) ||
            (t.purchase_id != null && String(t.purchase_id).toLowerCase().includes(term)) ||
            (t.ticket_type != null && String(t.ticket_type).toLowerCase().includes(term)) ||
            (t.order_id != null && String(t.order_id).toLowerCase().includes(term))
        );
    });

    // Total purchased should reflect booked seats, not just table rows.
    const totalPurchased = filteredTickets.reduce((sum, t) => sum + (toNumber(t.quantity, 1) || 1), 0);

    // Total revenue: price × quantity per row (API returns price and quantity)
    const totalAmount = filteredTickets.reduce((sum, t) => {
        const numericQty = toNumber(t.quantity, 1) || 1;
        const rowAmount = toNumber(t.amount, 0);
        if (rowAmount > 0) return sum + rowAmount;
        const numericPrice = toNumber(t.price, 0);
        return sum + (numericPrice * numericQty);
    }, 0);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Ticket Management</h2>
                    <p className="text-sm text-gray-500 mt-1">Manage and track all event ticket sales platform-wide.</p>
                </div>
            </div>
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center space-x-4 relative overflow-hidden group">
                    <div className="p-3 bg-indigo-50 rounded-xl text-indigo-600">
                        <Tag className="h-6 w-6" />
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Tickets Purchased</p>
                        <p className="text-2xl font-black text-gray-900 mt-1">{totalPurchased}</p>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center space-x-4 relative overflow-hidden group">
                    <div className="p-3 bg-emerald-50 rounded-xl text-emerald-600">
                        <DollarSign className="h-6 w-6" />
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Revenue Generated</p>
                        <p className="text-2xl font-black text-gray-900 mt-1">{formatCurrency(totalAmount)}</p>
                    </div>
                </div>
            </div>

            {error && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">{error}</div>
            )}

            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by event, customer, ticket type or order ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                    />
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg shadow-gray-100 overflow-hidden border border-gray-100">
                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-100">
                            <thead className="bg-gray-50/50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Customer</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Event</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest text-center">Qty</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Ticket Type</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Price</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Status</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Date</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-50">
                                {filteredTickets.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-12 text-center text-gray-400 font-medium">No records found.</td>
                                    </tr>
                                ) : (
                                    filteredTickets.map((ticket, index) => (
                                        <tr key={ticket.id ?? ticket.purchase_id ?? ticket.order_id ?? `ticket-${index}`} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="h-9 w-9 rounded-xl bg-indigo-50 flex items-center justify-center mr-3">
                                                        <User className="h-5 w-5 text-indigo-600" />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-bold text-gray-900">{getCustomerDisplay(ticket)}</div>
                                                        {(ticket.customer_email || ticket.email) && (
                                                            <div className="text-xs text-gray-400 font-medium">{ticket.customer_email ?? ticket.email}</div>
                                                        )}
                                                        {ticket.order_id != null && (
                                                            <div className="text-xs text-gray-400 font-medium">Order #{ticket.order_id}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center text-sm font-semibold text-gray-700">
                                                    <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                                                    {getEventDisplay(ticket)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-gray-600">
                                                {ticket.quantity ?? 1}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                                                    {getTicketTypeName(ticket, ticket.ticket_type ?? ticket.ticket_type_name)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{formatCurrency(ticket.price ?? 0)}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-3 py-1 text-[10px] font-bold uppercase tracking-widest rounded-full ${((ticket.status ?? ticket.payment_status) || '').toLowerCase() === 'completed' || (ticket.status ?? ticket.payment_status) === 'confirmed' || (ticket.status ?? ticket.payment_status) === 'paid'
                                                    ? 'bg-green-50 text-green-600 border border-green-100'
                                                    : 'bg-amber-50 text-amber-600 border border-amber-100'
                                                    }`}>
                                                    {ticket.status ?? ticket.payment_status ?? '—'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                                                {(ticket.purchase_date ?? ticket.created_at) ? new Date(ticket.purchase_date ?? ticket.created_at).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—'}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Tickets;
